import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { AuthenticationResult, EventMessage, EventType } from '@azure/msal-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { delay, filter, map, tap, catchError } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { ColorModeService } from '@coreui/angular-pro';
import { USER_SERVICE } from '@core/services/user.service.interface';
import { environment } from '../environments/environment';

@Component({
    selector: 'app-root',
    template: '<router-outlet />',
    standalone: true,
    imports: [RouterOutlet],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
    title = 'Master Data Loader';

    readonly destroyRef: DestroyRef = inject(DestroyRef);
    readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
    readonly titleService = inject(Title);
    readonly colorModeService = inject(ColorModeService);
    readonly msalService = inject(MsalService);
    readonly userService = inject(USER_SERVICE);
    readonly msalBroadcastService = inject(MsalBroadcastService);

    constructor() {
        this.titleService.setTitle(this.title);
        this.colorModeService.localStorageItemName.set('coreui-free-angular-admin-template-theme-default');
        this.colorModeService.eventName.set('ColorSchemeChange');
    }

    ngOnInit(): void {
        this.userService.clearUser();

        this.activatedRoute.queryParams
            .pipe(
                delay(1),
                map(params => params['theme']?.match(/^[A-Za-z0-9\s]+/)?.[0]),
                filter((theme): theme is string => theme !== undefined && ['dark', 'light', 'auto'].includes(theme)),
                tap(theme => {
                    this.colorModeService.colorMode.set(theme);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        // Success events: LOGIN_SUCCESS, ACQUIRE_TOKEN_SUCCESS
        this.msalBroadcastService.msalSubject$
            .pipe(
                filter(
                    (msg: EventMessage) =>
                        msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((result: EventMessage) => {
                const payload = result.payload as AuthenticationResult;
                this.msalService.instance.setActiveAccount(payload.account);
                this.userService.createUserFromAuthenticationResult(payload);
            });

        // Failure events: LOGIN_FAILURE, ACQUIRE_TOKEN_FAILURE
        this.msalBroadcastService.msalSubject$
            .pipe(
                filter(
                    (msg: EventMessage) =>
                        msg.eventType === EventType.LOGIN_FAILURE || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                // MSAL handles retry and redirect for transient failures.
                // Persistent failures leave the user on the MSAL redirect page.
            });

        const activeAccount = this.msalService.instance.getActiveAccount();
        if (activeAccount && !this.userService.currentUserValue) {
            this.msalService
                .acquireTokenSilent({
                    scopes: environment.msal.scopes,
                    account: activeAccount
                })
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    catchError(() => EMPTY)
                )
                .subscribe();
        }
    }
}
